/**
 * Theme Helper Functions
 * Utility functions for common theme-related operations
 */

import { Theme } from '@mui/material/styles';
import { MissionStatus } from '../types/mission';

/**
 * Get status color based on status string
 */
export const getStatusColor = (status: string, theme: Theme): { backgroundColor: string; color: string } => {
  const statusColorMap: Record<string, { backgroundColor: string; color: string }> = {
    [MissionStatus.PENDING]: {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.common.white,
    },
    [MissionStatus.PENDING_ADMIN_REVIEW]: {
      backgroundColor: theme.palette.warning.dark,
      color: theme.palette.warning.contrastText,
    },
    [MissionStatus.SUBMITTED]: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
    [MissionStatus.GHOST_ASSIGNED]: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    },
    [MissionStatus.MISSION_IN_PROGRESS]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    [MissionStatus.COMPLETED]: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    [MissionStatus.UNDER_REVIEW]: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    [MissionStatus.CLOSED]: {
      backgroundColor: theme.palette.grey[600],
      color: theme.palette.common.white,
    },
    [MissionStatus.CANCELLED]: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
    [MissionStatus.EXPIRED]: {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.common.white,
    },
    'action_taken': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    reviewed: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
    dismissed: {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.common.white,
    },
  };

  return statusColorMap[status] || {
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
  };
};

/**
 * Get priority color based on priority string
 */
export const getPriorityColor = (priority: 'high' | 'medium' | 'low', theme: Theme): { backgroundColor: string; color: string } => {
  switch (priority) {
    case 'high':
      return {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      };
    case 'medium':
      return {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
      };
    case 'low':
      return {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
      };
    default:
      return {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.secondary,
      };
  }
};

/**
 * Get moderation status color
 */
export const getModerationStatusColor = (status: string, theme: Theme): string => {
  const statusColorMap: Record<string, string> = {
    pending: theme.palette.warning.main,
    reviewed: theme.palette.info.main,
    action_taken: theme.palette.success.main,
    dismissed: theme.palette.grey[500],
  };
  return statusColorMap[status] || theme.palette.text.secondary;
};

/**
 * Get mission type color
 */
export const getMissionTypeColor = (type: string, theme: Theme): string => {
  const typeColorMap: Record<string, string> = {
    SnapAndGrab: theme.palette.info.light,
    Surveillance: theme.palette.secondary.main,
    Investigative: theme.palette.warning.main,
  };
  return typeColorMap[type] || theme.palette.grey[500];
};
